import { HTMLAttributes, ReactNode } from 'react';

export type ChipTone = 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info';

type ChipProps = {
  tone?: ChipTone;
  variant?: 'soft' | 'outline';
  icon?: ReactNode;
  children: ReactNode;
} & HTMLAttributes<HTMLSpanElement>;

export const Chip = ({
  tone = 'neutral',
  variant = 'soft',
  icon,
  children,
  className = '',
  ...rest
}: ChipProps) => (
  <span
    className={`ds-chip ${className}`}
    data-ds-tone={tone === 'neutral' ? undefined : tone}
    data-ds-variant={variant === 'outline' ? 'outline' : undefined}
    {...rest}
  >
    {icon}
    {children}
  </span>
);

type StatusChipProps = {
  /** The mandated hue for this status code. Passed through untouched. */
  color: string;
  label: string;
  className?: string;
};

/**
 * Status chip for codes whose colour is FIXED by the domain (MXI work-order
 * statuses, warranty states, certificate expiry).
 *
 * The rule this encodes: the mandated hue is never altered, but it also never
 * becomes the chip's background — it renders as a dot, so the label keeps the
 * theme's own text/background contrast no matter how bright or pale the hue is
 * (`status.pause` is #FFFF66; white text on it is unreadable, and a dark theme
 * with a #FF0000 fill is worse).
 *
 * It also means colour is never the only signal — the text label is always
 * present, which is what makes these readable for colour-blind users. That is
 * an addition, not a change: no hue is modified.
 */
export const StatusChip = ({ color, label, className = '' }: StatusChipProps) => (
  <span className={`ds-chip ${className}`}>
    <span
      className="ds-chip-dot"
      style={{ backgroundColor: color }}
      aria-hidden="true"
    />
    {label}
  </span>
);

export const Badge = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="ds-badge">{count > 99 ? '99+' : count}</span>
  ) : null;
